import { watch } from 'vue';
import { storeToRefs } from 'pinia';
import { eventBus } from '@/services/eventBus';
import type { CrossReferences, ReadinessSnapshot } from '@/types/integration';
import type { ChapterStoryContext } from '@/types/storyContext';
import type {
  ChapterGenerationContext as UnifiedChapterContext,
  ChapterContextOptions,
  WritingStyleContext,
  CharactersContext,
  PlotContext,
  PlotActContext,
  PlotTurningPointContext,
  WorldContext,
  WorldLocationContext,
  StoryElementsContext,
  NotesContext,
  RelationshipContext,
  ContextValidationResult
} from '@/types/chapterGeneration';
import type { Character, Relationship, PlotStructure, Act, World, Chapter } from '@/types';
import { useChaptersStore } from '@/stores/chapters';
import { useCharactersStore } from '@/stores/characters';
import { useManuscriptsStore } from '@/stores/manuscripts';
import { useNotesStore } from '@/stores/notes';
import { usePlotStore } from '@/stores/plot';
import { useRelationshipsStore } from '@/stores/relationships';
import { useStoryElementsStore } from '@/stores/storyElements';
import { useWorldStore } from '@/stores/world';
import { useWritingStyleStore } from '@/stores/writingStyle';
import { ERROR_STRATEGIES } from '@/services/errorStrategies';

interface ReferenceCacheKey {
  manuscriptId: string;
  entityType: string;
  entityId: string;
}

const referenceKey = ({ manuscriptId, entityType, entityId }: ReferenceCacheKey): string => {
  return `${manuscriptId}:${entityType}:${entityId}`;
};

const clampNumber = (value: number): number => Math.round(value * 100) / 100;

class IntegrationService {
  private initialized = false;
  private chapterContexts = new Map<string, ChapterStoryContext>();
  private readinessSnapshots = new Map<string, ReadinessSnapshot>();
  private referenceCache = new Map<string, CrossReferences>();
  private unsubscribers: Array<() => void> = [];
  private stopHandles: Array<() => void> = [];

  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.bootstrapChapterSync();
    this.bootstrapReadinessSync();
    this.bootstrapReferenceSync();
    this.initialized = true;
  }

  dispose(): void {
    this.unsubscribers.forEach((unsubscribe) => unsubscribe());
    this.stopHandles.forEach((stop) => stop());
    this.unsubscribers = [];
    this.stopHandles = [];
    this.initialized = false;
    this.chapterContexts.clear();
    this.readinessSnapshots.clear();
    this.referenceCache.clear();
  }

  getChapterContext(manuscriptId: string, chapterId: string): ChapterStoryContext | null;
  getChapterContext(chapterNumber: number, options?: ChapterContextOptions): UnifiedChapterContext;
  getChapterContext(
    arg1: string | number,
    arg2?: string | ChapterContextOptions
  ): ChapterStoryContext | UnifiedChapterContext | null {
    if (typeof arg1 === 'string' && typeof arg2 === 'string') {
      const manuscriptId = arg1;
      const chapterId = arg2;
      const cached = this.chapterContexts.get(`${manuscriptId}:${chapterId}`);
      if (cached) {
        return cached;
      }
      const chaptersStore = useChaptersStore();
      const { activeChapterId } = storeToRefs(chaptersStore);
      if (activeChapterId.value !== chapterId) {
        return null;
      }
      const context = chaptersStore.getChapterGenerationContext();
      this.cacheChapterContext(manuscriptId, chapterId, context);
      return context;
    }

    const chapterNumber = Number(arg1);
    if (!Number.isFinite(chapterNumber) || chapterNumber <= 0) {
      throw new TypeError('Chapter number must be a positive integer.');
    }
    const options = (typeof arg2 === 'object' && arg2 !== null ? arg2 : {}) as ChapterContextOptions;
    return this.buildUnifiedChapterContext(Math.floor(chapterNumber), options);
  }

  getReadinessSnapshot(manuscriptId: string): ReadinessSnapshot | null {
    const cached = this.readinessSnapshots.get(manuscriptId);
    if (cached) {
      return cached;
    }
    const snapshot = this.recalculateReadiness(manuscriptId);
    return snapshot ?? null;
  }

  getCrossReferences(manuscriptId: string, entityType: string, entityId: string): CrossReferences {
    const key = referenceKey({ manuscriptId, entityType, entityId });
    const cached = this.referenceCache.get(key);
    if (cached) {
      return cached;
    }
    const references = this.buildCrossReferences(manuscriptId, entityType, entityId);
    this.referenceCache.set(key, references);
    return references;
  }

  validateChapterContext(chapterNumber: number): ContextValidationResult {
    const manuscriptId = this.ensureActiveManuscriptId();
    if (!Number.isFinite(chapterNumber) || chapterNumber <= 0) {
      throw new TypeError('Chapter number must be a positive integer.');
    }

    const issues: string[] = [];
    const warnings: string[] = [];
    const contextState = {
      writingStyle: false,
      characters: false,
      plot: false,
      world: false,
      storyElements: false,
      notes: false,
      readiness: this.readinessSnapshots.has(manuscriptId),
      crossReferences: this.referenceCache.size > 0
    };

    const writingStyleStore = useWritingStyleStore();
    if (writingStyleStore.setActiveManuscript) {
      writingStyleStore.setActiveManuscript(manuscriptId);
    }
    const writingStyleRef = writingStyleStore.writingStyle;
    const writingStyle = writingStyleRef ? writingStyleRef.value : null;
    if (!writingStyle) {
      warnings.push('MISSING_WRITING_STYLE');
    } else {
      contextState.writingStyle = true;
      if (!writingStyle.exampleParagraphs || writingStyle.exampleParagraphs.length === 0) {
        warnings.push('Writing style has no example paragraphs. Voice matching may be inconsistent.');
      }
      if (!writingStyle.styleRules || writingStyle.styleRules.length === 0) {
        warnings.push('Writing style has no rules defined. Tone constraints will be relaxed.');
      }
    }

    const charactersStore = useCharactersStore();
    if (charactersStore.setActiveManuscript) {
      charactersStore.setActiveManuscript(manuscriptId);
    }
    const characters = charactersStore.getCharactersForManuscript(manuscriptId);
    if (characters.length === 0) {
      issues.push('MISSING_CHARACTERS');
    } else {
      contextState.characters = true;
      const charactersMissingVoices = characters.filter(
        (character) => !character.voice || character.voice.exampleDialogue.trim().length === 0
      );
      if (charactersMissingVoices.length > 0) {
        warnings.push('INSUFFICIENT_CHARACTER_DATA');
      }
    }

    const plotStore = usePlotStore();
    if (plotStore.setActiveManuscript) {
      plotStore.setActiveManuscript(manuscriptId);
    }
    const plot = plotStore.getPlotByManuscript(manuscriptId);
    if (!plot) {
      warnings.push('MISSING_PLOT');
    } else {
      contextState.plot = true;
      if (!plot.acts || plot.acts.length === 0) {
        warnings.push('Plot has no acts defined. Consider completing the act structure for better pacing guidance.');
      }
    }

    const worldStore = useWorldStore();
    if (worldStore.setActiveManuscript) {
      worldStore.setActiveManuscript(manuscriptId);
    }
    const world = worldStore.getWorldByManuscript(manuscriptId);
    if (!world) {
      warnings.push('MISSING_WORLD');
    } else {
      contextState.world = true;
    }

    const storyElementsStore = useStoryElementsStore();
    if (storyElementsStore.setActiveManuscript) {
      storyElementsStore.setActiveManuscript(manuscriptId);
    }
    const storyElementsRef = storyElementsStore.storyElements;
    const storyElements = storyElementsRef ? storyElementsRef.value : null;
    if (storyElements) {
      contextState.storyElements = true;
    } else {
      warnings.push('Story elements are not configured. Recurring object and narrative rule continuity will be skipped.');
    }

    const chaptersStore = useChaptersStore();
    const { chapter } = this.getChapterMetadata(chapterNumber);
    if (!chapter) {
      warnings.push('Chapter number does not match an existing chapter. Generation will treat this as a new draft.');
    }

    const notesStore = useNotesStore();
    if (notesStore.setActiveManuscript) {
      notesStore.setActiveManuscript(manuscriptId);
    }
    const { notes } = storeToRefs(notesStore);
    contextState.notes = (notes.value ?? []).some((note) => note.manuscriptId === manuscriptId);

    const pendingConfirmations =
      warnings.filter((code) => Boolean(ERROR_STRATEGIES[code as keyof typeof ERROR_STRATEGIES]?.requiresConfirmation));

    return {
      canProceed: issues.length === 0,
      issues,
      warnings,
      pendingConfirmations: pendingConfirmations.length > 0 ? pendingConfirmations : undefined,
      context: contextState
    };
  }

  private buildUnifiedChapterContext(
    chapterNumber: number,
    options: ChapterContextOptions
  ): UnifiedChapterContext {
    const manuscriptId = this.ensureActiveManuscriptId();
    const normalizedOptions = this.normalizeOptions(options);
    const chapterMeta = this.getChapterMetadata(chapterNumber);
    const context: UnifiedChapterContext = {
      manuscriptId,
      chapterNumber,
      timestamp: new Date().toISOString()
    };

    if (normalizedOptions.includeWritingStyle) {
      const writingStyleContext = this.getWritingStyleContext(manuscriptId);
      if (writingStyleContext) {
        context.writingStyle = writingStyleContext;
      }
    }

    let charactersContext: CharactersContext | undefined;
    if (normalizedOptions.includeCharacters) {
      charactersContext = this.getCharactersContext(manuscriptId, chapterMeta.chapterId, chapterNumber);
      if (charactersContext) {
        context.characters = charactersContext;
      }
    }

    let plotContext: PlotContext | undefined;
    if (normalizedOptions.includePlot) {
      plotContext = this.getPlotContext(manuscriptId, chapterNumber, chapterMeta);
      if (plotContext) {
        context.plot = plotContext;
      }
    }

    if (normalizedOptions.includeWorld) {
      const worldContext = this.getWorldContext(manuscriptId, chapterMeta.chapterId);
      if (worldContext) {
        context.world = worldContext;
      }
    }

    if (normalizedOptions.includeStoryElements) {
      const storyElementsContext = this.getStoryElementsContext(manuscriptId);
      if (storyElementsContext) {
        context.storyElements = storyElementsContext;
      }
    }

    if (normalizedOptions.includeNotes) {
      const actNumber = plotContext?.currentAct?.number ?? null;
      const notesContext = this.getNotesContext(manuscriptId, chapterNumber, actNumber);
      if (notesContext) {
        context.notes = notesContext;
      }
    }

    return context;
  }

  private normalizeOptions(options: ChapterContextOptions): Required<ChapterContextOptions> {
    return {
      includeWritingStyle: options.includeWritingStyle !== false,
      includeCharacters: options.includeCharacters !== false,
      includePlot: options.includePlot !== false,
      includeWorld: options.includeWorld !== false,
      includeStoryElements: options.includeStoryElements !== false,
      includeNotes: options.includeNotes !== false
    };
  }

  private ensureActiveManuscriptId(): string {
    const manuscriptsStore = useManuscriptsStore();
    const { activeManuscriptId } = storeToRefs(manuscriptsStore);
    if (!activeManuscriptId.value) {
      throw new Error('Cannot build chapter context without an active manuscript.');
    }
    return activeManuscriptId.value;
  }

  private getChapterMetadata(chapterNumber: number): {
    chapters: Chapter[];
    chapter: Chapter | null;
    chapterId: string | null;
    chapterCount: number;
  } {
    const chaptersStore = useChaptersStore();
    const { sortedChapters } = storeToRefs(chaptersStore);
    const chapters = sortedChapters.value;
    const chapter = chapters.find((item) => item.number === chapterNumber) ?? null;
    return {
      chapters,
      chapter,
      chapterId: chapter?.id ?? null,
      chapterCount: chapters.length
    };
  }

  private getWritingStyleContext(manuscriptId: string): WritingStyleContext | undefined {
    const writingStyleStore = useWritingStyleStore();
    if (writingStyleStore.setActiveManuscript) {
      writingStyleStore.setActiveManuscript(manuscriptId);
    }
    const { writingStyle } = storeToRefs(writingStyleStore);
    const raw = writingStyle.value;
    if (!raw) {
      return undefined;
    }

    return {
      profile: {
        name: raw.styleProfile.name,
        essence: raw.styleProfile.essence,
        pov: raw.styleProfile.pov,
        tense: raw.styleProfile.tense,
        sentenceLength: raw.styleProfile.sentenceLength,
        vocabulary: raw.styleProfile.vocabulary,
        tone: raw.styleProfile.tone,
        narrativeDistance: raw.styleProfile.narrativeDistance
      },
      exampleParagraphs: raw.exampleParagraphs.map((paragraph) => ({
        id: paragraph.id,
        label: paragraph.label,
        text: paragraph.text,
        context: paragraph.context,
        notes: paragraph.notes
      })),
      styleRules: raw.styleRules.map((rule) => ({
        id: rule.id,
        rule: rule.rule,
        severity: rule.severity,
        rationale: rule.rationale,
        goodExample: rule.goodExample,
        badExample: rule.badExample
      }))
    };
  }

  private getCharactersContext(
    manuscriptId: string,
    chapterId: string | null,
    chapterNumber: number
  ): CharactersContext | undefined {
    const charactersStore = useCharactersStore();
    if (charactersStore.setActiveManuscript) {
      charactersStore.setActiveManuscript(manuscriptId);
    }
    const characters = charactersStore.getCharactersForManuscript(manuscriptId);
    if (characters.length === 0) {
      return undefined;
    }

    const relevantCharacters = this.filterRelevantCharacters(characters, chapterId, chapterNumber);
    if (relevantCharacters.length === 0) {
      return undefined;
    }

    const mappedCharacters = relevantCharacters.map((character) => {
      const details = character.narrativeDetails ?? {};
      return {
        id: character.id,
        name: character.name,
        role: character.role,
        age: character.age,
        gender: character.gender,
        importance: character.importance,
        summary: character.characterSummary,
        coreIdentity: {
          essence: details['core-essence'] ?? '',
          want: details['external-want'] ?? '',
          need: details['internal-need'] ?? '',
          fear: details['deepest-fear'] ?? '',
          flaw: details['fatal-flaw'] ?? ''
        },
        physical: {
          appearance: details['physical-appearance'] ?? '',
          mannerisms: details['distinctive-mannerisms'] ?? '',
          firstImpression: details['first-impression'] ?? ''
        },
        voice: {
          speakingStyle: character.voice.speakingStyle,
          vocabularyLevel: character.voice.vocabularyLevel,
          sentenceStructure: character.voice.sentenceStructure,
          commonPhrases: [...character.voice.commonPhrases],
          verbalTics: [...character.voice.verbalTics],
          exampleDialogue: character.voice.exampleDialogue,
          accentOrDialect: character.voice.accentOrDialect
        },
        skills: {
          expertAt: [...character.skills.expertAt],
          cannotDo: [...character.skills.cannotDo],
          specialAbilities: character.skills.specialAbilities ? [...character.skills.specialAbilities] : undefined
        },
        emotionalPatterns: { ...character.emotionalPatterns },
        backstory: {
          keyEvent: details['formative-event'] ?? '',
          currentSituation: details['current-situation'] ?? ''
        }
      };
    });

    const relationshipsStore = useRelationshipsStore();
    if (relationshipsStore.setActiveManuscript) {
      relationshipsStore.setActiveManuscript(manuscriptId);
    }
    const relationships = relationshipsStore.getRelationshipsForManuscript(manuscriptId);
    const relationshipContext = this.buildRelationshipContext(relevantCharacters, relationships);

    return {
      activeCharacters: mappedCharacters,
      relationships: relationshipContext
    };
  }

  private filterRelevantCharacters(
    characters: Character[],
    chapterId: string | null,
    chapterNumber: number
  ): Character[] {
    if (chapterId) {
      const appearing = characters.filter(
        (character) =>
          character.appearances.chapterIds.includes(chapterId) ||
          character.appearances.mentionedInChapterIds.includes(chapterId)
      );
      if (appearing.length > 0) {
        return appearing;
      }
    }

    const prioritized = characters.filter(
      (character) => character.importance === 'major' || character.importance === 'supporting'
    );
    if (prioritized.length > 0) {
      return prioritized;
    }

    return characters.slice(0, Math.min(5, characters.length));
  }

  private buildRelationshipContext(
    characters: Character[],
    relationships: Relationship[]
  ): RelationshipContext[] {
    if (relationships.length === 0) {
      return [];
    }

    const characterMap = new Map<string, Character>();
    characters.forEach((character) => characterMap.set(character.id, character));
    const relevantIds = new Set(characters.map((character) => character.id));

    return relationships
      .filter(
        (relationship) =>
          relevantIds.has(relationship.characterAId) && relevantIds.has(relationship.characterBId)
      )
      .map((relationship) => {
        const characterA = characterMap.get(relationship.characterAId);
        const characterB = characterMap.get(relationship.characterBId);
        return {
          characterA: characterA?.name ?? relationship.characterAId,
          characterB: characterB?.name ?? relationship.characterBId,
          category: relationship.category,
          currentStatus: relationship.currentStatus,
          dynamicQualities: {
            intensity: relationship.intensity,
            trustLevel: relationship.trustLevel,
            powerDynamic: relationship.powerDynamic,
            emotionalTone: relationship.emotionalTone,
            dramaTypes: [...relationship.dramaTypes],
            trajectory: relationship.trajectory
          },
          historyBetween: relationship.sharedHistory ?? ''
        };
      });
  }

  private getPlotContext(
    manuscriptId: string,
    chapterNumber: number,
    chapterMeta: { chapter: Chapter | null; chapterCount: number }
  ): PlotContext | undefined {
    const plotStore = usePlotStore();
    if (plotStore.setActiveManuscript) {
      plotStore.setActiveManuscript(manuscriptId);
    }
    const plot = plotStore.getPlotByManuscript(manuscriptId);
    if (!plot) {
      return undefined;
    }

    const currentAct = this.getActForChapter(plot, chapterNumber, chapterMeta.chapterCount);
    const allTurningPoints = plot.acts.flatMap((act) =>
      act.turningPoints.map((turningPoint) => ({
        id: turningPoint.id,
        title: turningPoint.title,
        description: turningPoint.description,
        changeType: turningPoint.changeType,
        impact: turningPoint.impact,
        chapterId: turningPoint.chapterId
      }))
    );

    const relevantTurningPoints = allTurningPoints.filter((turningPoint) => {
      if (!currentAct) {
        return false;
      }
      const owningAct = plot.acts.find((act) => act.turningPoints.some((tp) => tp.id === turningPoint.id));
      return owningAct?.id === currentAct.original.id;
    });

    const recentTurningPoints = this.getRecentTurningPoints(plot);

    return {
      overview: plot.overview,
      currentAct: currentAct?.context ?? null,
      turningPoints: relevantTurningPoints,
      recentTurningPoints
    };
  }

  private getActForChapter(
    plot: PlotStructure,
    chapterNumber: number,
    chapterCount: number
  ): { context: PlotActContext; original: Act } | null {
    if (!plot.acts || plot.acts.length === 0) {
      return null;
    }

    const totalActs = plot.acts.length;
    const chaptersPerAct = Math.max(1, Math.round(chapterCount / totalActs));
    const actIndex = Math.min(totalActs - 1, Math.floor((chapterNumber - 1) / chaptersPerAct));
    const act = plot.acts[actIndex];

    return {
      original: act,
      context: {
        number: act.number,
        title: act.title,
        purpose: act.purpose,
        protagonistGoal: act.protagonistGoal,
        opposition: act.opposition,
        actQuestion: act.actQuestion,
        startingEmotion: act.startingEmotion,
        endingEmotion: act.endingEmotion,
        internalConflict: act.internalConflict,
        externalConflict: act.externalConflict,
        actStakes: act.actStakes,
        tensionLevel: act.tensionLevel
      }
    };
  }

  private getRecentTurningPoints(plot: PlotStructure): PlotTurningPointContext[] {
    const turningPoints = plot.acts
      .flatMap((act) =>
        act.turningPoints.map((turningPoint) => ({
          id: turningPoint.id,
          title: turningPoint.title,
          description: turningPoint.description,
          changeType: turningPoint.changeType,
          impact: turningPoint.impact,
          chapterId: turningPoint.chapterId,
          actOrder: act.number,
          order: turningPoint.order
        }))
      )
      .sort((a, b) => {
        if (a.actOrder === b.actOrder) {
          return a.order - b.order;
        }
        return a.actOrder - b.actOrder;
      });

    return turningPoints
      .slice(-3)
      .map(({ actOrder, order, ...rest }) => rest)
      .reverse();
  }

  private getWorldContext(manuscriptId: string, chapterId: string | null): WorldContext | undefined {
    const worldStore = useWorldStore();
    if (worldStore.setActiveManuscript) {
      worldStore.setActiveManuscript(manuscriptId);
    }
    const world = worldStore.getWorldByManuscript(manuscriptId);
    if (!world) {
      return undefined;
    }

    const currentLocation = this.pickCurrentLocation(world, chapterId);
    return {
      overview: world.overview,
      rules: world.rules,
      currentLocation,
      culture: world.culture,
      capabilities: world.capabilities
    };
  }

  private pickCurrentLocation(world: World, chapterId: string | null): WorldLocationContext | null {
    if (!world.locations || world.locations.length === 0) {
      return null;
    }

    const preciseMatch =
      chapterId !== null
        ? world.locations.find((location) => location.chapterIds?.includes(chapterId))
        : undefined;

    const candidate =
      preciseMatch ??
      world.locations.find((location) => location.importance === 'primary') ??
      world.locations[0];

    return {
      id: candidate.id,
      name: candidate.name,
      category: candidate.type,
      significance: candidate.importance,
      atmosphere: candidate.atmosphere,
      sensoryProfile: {
        visualStyle: candidate.sensoryProfile.visualStyle,
        sounds: candidate.sensoryProfile.sounds,
        smells: candidate.sensoryProfile.smells,
        tactileFeeling: candidate.sensoryProfile.tactileFeeling,
        notableFeatures: [...candidate.sensoryProfile.notableFeatures],
        timeOfDayChanges: candidate.sensoryProfile.timeOfDayChanges,
        weatherImpact: candidate.sensoryProfile.weatherImpact
      }
    };
  }

  private getStoryElementsContext(manuscriptId: string): StoryElementsContext | undefined {
    const storyElementsStore = useStoryElementsStore();
    if (storyElementsStore.setActiveManuscript) {
      storyElementsStore.setActiveManuscript(manuscriptId);
    }
    const { storyElements } = storeToRefs(storyElementsStore);
    const elements = storyElements.value;
    if (!elements) {
      return undefined;
    }

    return {
      recurringObjects: elements.recurringObjects.map((object) => ({
        id: object.id,
        name: object.name,
        description: object.description,
        significance: object.significance,
        importance: object.importance,
        currentLocation: object.currentLocation ?? '',
        status: object.status
      })),
      sensorySignatures: elements.sensorySignatures.map((signature) => ({
        id: signature.id,
        name: signature.name,
        description: signature.description,
        scope: signature.scope,
        scopeName: signature.scopeName,
        trigger: signature.trigger,
        frequency: signature.frequency
      })),
      narrativeRules: elements.narrativeRules.map((rule) => ({
        id: rule.id,
        rule: rule.rule,
        severity: rule.severity,
        appliesTo: rule.appliesTo,
        actIds: rule.actIds ? [...rule.actIds] : undefined,
        characterIds: rule.characterIds ? [...rule.characterIds] : undefined,
        rationale: rule.rationale,
        goodExample: rule.goodExample,
        badExample: rule.badExample
      }))
    };
  }

  private getNotesContext(
    manuscriptId: string,
    chapterNumber: number,
    actNumber: number | null
  ): NotesContext | undefined {
    const notesStore = useNotesStore();
    if (notesStore.setActiveManuscript) {
      notesStore.setActiveManuscript(manuscriptId);
    }
    const { notes } = storeToRefs(notesStore);
    const collection = notes.value ?? [];
    if (collection.length === 0) {
      return undefined;
    }

    const chapterTag = `chapter-${chapterNumber}`;
    const actTag = actNumber ? `act-${actNumber}` : null;
    const relevantNotes = collection.filter(
      (note) =>
        note.manuscriptId === manuscriptId &&
        (note.tags.includes(chapterTag) || (actTag !== null && note.tags.includes(actTag)))
    );

    if (relevantNotes.length === 0) {
      return undefined;
    }

    return {
      relevantNotes: relevantNotes.map((note) => ({
        id: note.id,
        text: note.text,
        tags: [...note.tags],
        icon: note.icon,
        updatedAt: note.updatedAt
      }))
    };
  }

  private bootstrapChapterSync(): void {
    const chaptersStore = useChaptersStore();
    const manuscriptsStore = useManuscriptsStore();
    const { activeChapterId } = storeToRefs(chaptersStore);
    const { activeManuscriptId } = storeToRefs(manuscriptsStore);

    const stopWatch = watch(
      () => ({
        chapterId: activeChapterId.value,
        manuscriptId: activeManuscriptId.value
      }),
      ({ chapterId, manuscriptId }) => {
        if (!manuscriptId) {
          return;
        }

        if (!chapterId) {
          this.clearChapterContextsForManuscript(manuscriptId);
          return;
        }

        const context = chaptersStore.getChapterGenerationContext();
        this.cacheChapterContext(manuscriptId, chapterId, context);
        eventBus.emit('chapter:contextChanged', { manuscriptId, chapterId, context });
      },
      { immediate: true }
    );

    this.stopHandles.push(stopWatch);

    this.unsubscribers.push(
      eventBus.on('manuscript:activeChapterChanged', ({ manuscriptId, chapterId }) => {
        if (!manuscriptId || !chapterId) {
          if (manuscriptId) {
            this.clearChapterContextsForManuscript(manuscriptId);
          }
          return;
        }

        if (activeChapterId.value === chapterId) {
          const key = `${manuscriptId}:${chapterId}`;
          if (this.chapterContexts.has(key)) {
            return;
          }
        }

        const context = chaptersStore.getChapterGenerationContext();
        this.cacheChapterContext(manuscriptId, chapterId, context);
        eventBus.emit('chapter:contextChanged', { manuscriptId, chapterId, context });
      })
    );
  }

  private bootstrapReadinessSync(): void {
    const manuscriptsStore = useManuscriptsStore();
    const { activeManuscriptId } = storeToRefs(manuscriptsStore);

    const stop = watch(
      activeManuscriptId,
      (manuscriptId) => {
        if (!manuscriptId) {
          return;
        }
        this.recalculateReadiness(manuscriptId);
      },
      { immediate: true }
    );
    this.stopHandles.push(stop);

    this.unsubscribers.push(
      eventBus.on('aiReadiness:changed', ({ manuscriptId }) => {
        this.recalculateReadiness(manuscriptId);
      })
    );
  }

  private bootstrapReferenceSync(): void {
    this.unsubscribers.push(
      eventBus.on('entity:referencesUpdated', ({ manuscriptId, entityType, entityId }) => {
        const key = referenceKey({ manuscriptId, entityType, entityId });
        this.referenceCache.delete(key);
        const references = this.buildCrossReferences(manuscriptId, entityType, entityId);
        this.referenceCache.set(key, references);
        eventBus.emit('entity:referencesHydrated', { manuscriptId, entityType, entityId, references });
      })
    );

    this.unsubscribers.push(
      eventBus.on('character:changed', ({ manuscriptId, characterId }) => {
        const key = referenceKey({ manuscriptId, entityType: 'character', entityId: characterId });
        this.referenceCache.delete(key);
      })
    );
  }

  private cacheChapterContext(manuscriptId: string, chapterId: string, context: ChapterStoryContext): void {
    const key = `${manuscriptId}:${chapterId}`;
    this.chapterContexts.set(key, context);
  }

  private clearChapterContextsForManuscript(manuscriptId: string): void {
    const keys = Array.from(this.chapterContexts.keys()).filter((key) => key.startsWith(`${manuscriptId}:`));
    keys.forEach((key) => this.chapterContexts.delete(key));
  }

  private recalculateReadiness(manuscriptId: string): ReadinessSnapshot | null {
    const charactersStore = useCharactersStore();
    const plotStore = usePlotStore();
    const worldStore = useWorldStore();

    const readinessMap = charactersStore.getReadinessForManuscript(manuscriptId);
    const characterScores = Object.values(readinessMap)
      .map((entry) => entry?.overall ?? null)
      .filter((value): value is number => typeof value === 'number');

    const characterAverage =
      characterScores.length > 0
        ? clampNumber(characterScores.reduce((sum, value) => sum + value, 0) / characterScores.length)
        : null;

    const plot = plotStore.getPlotByManuscript(manuscriptId);
    const plotScore = plot?.aiReadiness?.overall ?? null;

    const world = worldStore.getWorldByManuscript(manuscriptId);
    const worldScore = world?.aiReadiness?.overall ?? null;

    if (characterAverage === null && plotScore === null && worldScore === null) {
      return null;
    }

    const snapshot: ReadinessSnapshot = {
      characters: characterAverage,
      plot: plotScore,
      world: worldScore,
      updatedAt: new Date().toISOString()
    };

    this.readinessSnapshots.set(manuscriptId, snapshot);
    eventBus.emit('aiReadiness:snapshot', { manuscriptId, snapshot });
    return snapshot;
  }

  private buildCrossReferences(manuscriptId: string, entityType: string, entityId: string): CrossReferences {
    if (entityType !== 'character') {
      return {
        notes: [],
        relationships: [],
        storyElements: {
          recurringObjects: [],
          sensorySignatures: [],
          narrativeRules: []
        },
        chapters: []
      };
    }

    const charactersStore = useCharactersStore();
    const notesStore = useNotesStore();
    const relationshipsStore = useRelationshipsStore();
    const storyElementsStore = useStoryElementsStore();
    const chaptersStore = useChaptersStore();

    const characters = charactersStore.getCharactersForManuscript(manuscriptId);
    const targetCharacter = characters.find((character) => character.id === entityId);

    const relationships = relationshipsStore
      .getRelationshipsForManuscript(manuscriptId)
      .filter((relationship) => relationship.characterAId === entityId || relationship.characterBId === entityId)
      .map((relationship) => {
        const counterpartId =
          relationship.characterAId === entityId ? relationship.characterBId : relationship.characterAId;
        const counterpart = characters.find((character) => character.id === counterpartId);
        return {
          id: relationship.id,
          withCharacterId: counterpartId,
          withCharacterName: counterpart?.name ?? 'Unknown',
          category: relationship.category
        };
      });

    const noteTag = `character:${entityId}`;
    const { notes } = storeToRefs(notesStore);
    const filteredNotes = notes.value.filter((note) => note.tags.includes(noteTag));
    const notesReferences = filteredNotes
      .map((note) => ({
        id: note.id,
        preview: note.text.slice(0, 160),
        tags: [...note.tags],
        updatedAt: note.updatedAt
      }));

    const { storyElements } = storeToRefs(storyElementsStore);
    const storyElementsValue = storyElements.value;
    const storyElementReferences = {
      recurringObjects:
        storyElementsValue?.recurringObjects
          .filter((object) => object.characterId === entityId)
          .map((object) => ({
            id: object.id,
            name: object.name
          })) ?? [],
      sensorySignatures:
        storyElementsValue?.sensorySignatures
          .filter((signature) => signature.scope === 'character' && signature.scopeId === entityId)
          .map((signature) => ({
            id: signature.id,
            name: signature.name
          })) ?? [],
      narrativeRules:
        storyElementsValue?.narrativeRules
          .filter((rule) => rule.characterIds?.includes(entityId))
          .map((rule) => ({
            id: rule.id,
            name: rule.rule
          })) ?? []
    };

    const chapters = targetCharacter
      ? [
          ...targetCharacter.appearances.chapterIds.map((chapterId) => {
            const chapter = chaptersStore.getChapterById(chapterId);
            return chapter
              ? {
                  id: chapter.id,
                  number: chapter.number,
                  title: chapter.title
                }
              : null;
          }),
          ...targetCharacter.appearances.mentionedInChapterIds.map((chapterId) => {
            const chapter = chaptersStore.getChapterById(chapterId);
            return chapter
              ? {
                  id: chapter.id,
                  number: chapter.number,
                  title: chapter.title
                }
              : null;
          })
        ].filter((entry): entry is { id: string; number: number; title: string } => entry !== null)
      : [];

    return {
      notes: notesReferences,
      relationships,
      storyElements: storyElementReferences,
      chapters
    };
  }
}

export const integrationService = new IntegrationService();

